import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { decodeLinkKey } from '@sss/crypto';
import type { RoomEndReason } from '@sss/types';
import FlameMark from '../components/Brand/FlameMark';
import secretStyles from '../components/Secret/Secret.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import { ICE_SERVERS } from '../lib/config';
import { FileReceiver, type ReceiveFailure } from '../lib/fileHandoff';
import { canSaveToDisk, memorySink, pickDiskSink, type SavedFile } from '../lib/fileSink';
import { RELAY_MAX_TOTAL_BYTES, parseManifest, totalBytes, type FileEntry } from '../lib/fileTransfer';
import { HandoffConnection, type Transport } from '../lib/handoffConnection';
import { formatCode } from '../lib/handoffProtocol';
import { getRoom, roomSocketUrl } from '../lib/roomsApi';
import { forgetFragment, readFragment } from '../lib/secretLinks';
import { SecretsApiError } from '../lib/secretsApi';
import { formatBytes, safeFileName } from '../lib/tools';
import ui from '../styles/ui.module.css';
import styles from './LivePage.module.css';
import revealStyles from './RevealPage.module.css';

/**
 * /f/:id#<roomKey> — receive files live (docs/adr/0011). As /h/:id, loading
 * the page never joins the room; Receive does. The sender's manifest comes
 * first; nothing is sent until the recipient picks where to save (Chrome,
 * Edge: straight to disk) or accepts into memory (other browsers).
 */

/** Above this, warn that a browser without disk saving keeps everything in memory. */
const MEMORY_WARNING_BYTES = 500 * 1024 * 1024;

type Phase = 'connecting' | 'waitingSender' | 'securing' | 'receiving';

type State =
  | { step: 'checking' }
  | { step: 'incomplete' }
  | { step: 'gone' }
  | { step: 'ready' }
  | { step: 'live'; phase: Phase; code: string | null }
  | { step: 'offer'; code: string; files: FileEntry[]; noFolder: boolean }
  | { step: 'transferring'; files: FileEntry[]; written: number; total: number }
  | { step: 'received'; saved: SavedFile[]; disk: boolean }
  | { step: 'ended'; reason: Exclude<RoomEndReason, 'acknowledged' | 'delivered'> | 'lost' | 'noDirect' }
  | { step: 'failed'; reason: 'network' | 'rateLimited' | ReceiveFailure };

export default function FilesReceivePage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [state, setState] = useState<State>({ step: 'checking' });
  const [acknowledged, setAcknowledged] = useState(false);
  const [senderGone, setSenderGone] = useState(false);
  const roomKey = useRef<Uint8Array | null>(null);
  const connection = useRef<HandoffConnection | null>(null);
  const receiver = useRef<FileReceiver | null>(null);
  const transport = useRef<Transport | null>(null);
  const finished = useRef(false);
  const urls = useRef<string[]>([]);
  const heading = useRef<HTMLHeadingElement>(null);
  usePageTitle(t('pageTitle.files'));

  useEffect(() => {
    try {
      const fragment = readFragment(window.location.hash);
      roomKey.current = fragment ? decodeLinkKey(fragment) : null;
    } catch {
      roomKey.current = null;
    }
    if (!roomKey.current) {
      setState({ step: 'incomplete' });
      return;
    }
    let cancelled = false;
    getRoom(id)
      .then(() => !cancelled && setState({ step: 'ready' }))
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof SecretsApiError && err.type === 'gone') {
          forgetFragment();
          setState({ step: 'gone' });
        } else setState({ step: 'failed', reason: err instanceof SecretsApiError && err.type === 'rateLimited' ? 'rateLimited' : 'network' });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Leaving drops the connection, any half-written file, and the in-memory copies.
  useEffect(
    () => () => {
      void receiver.current?.cancel();
      connection.current?.close();
      for (const url of urls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  useEffect(() => {
    if (['offer', 'received', 'gone', 'ended', 'failed'].includes(state.step)) heading.current?.focus();
  }, [state.step]);

  const stopWith = (next: State) => {
    void receiver.current?.cancel();
    receiver.current = null;
    connection.current?.close();
    setState(next);
  };

  function receive() {
    const key = roomKey.current;
    if (!key) return;
    transport.current = null;
    setState({ step: 'live', phase: 'connecting', code: null });
    const live = (patch: Partial<Extract<State, { step: 'live' }>>) =>
      setState((current) => (current.step === 'live' ? { ...current, ...patch } : current));
    const conn = new HandoffConnection({
      roomId: id,
      roomKey: key,
      role: 'recipient',
      socketUrl: roomSocketUrl(id, 'recipient', window.location.origin),
      iceServers: ICE_SERVERS,
      events: {
        joined: () => live({ phase: 'waitingSender' }),
        peer: (present) => {
          if (finished.current) {
            if (!present) setSenderGone(true);
            return;
          }
          // A half-done transfer can't resume: the sender starts over with whoever is there.
          void receiver.current?.cancel();
          receiver.current = null;
          setState({ step: 'live', phase: present ? 'securing' : 'waitingSender', code: null });
        },
        secure: (code) => live({ phase: 'receiving', code }),
        transport: (settled) => {
          transport.current = settled;
        },
        record: (type, body) => {
          if (type === 'manifest') {
            const files = parseManifest(body);
            if (!files || receiver.current || finished.current) return;
            if (transport.current === 'relay' && totalBytes(files) > RELAY_MAX_TOTAL_BYTES) {
              // Leaving frees the seat: the link stays open, and Try again rejoins with a fresh connection.
              stopWith({ step: 'ended', reason: 'noDirect' });
              return;
            }
            setState((current) => ({ step: 'offer', code: current.step === 'live' ? (current.code ?? '') : '', files, noFolder: false }));
          } else if (type === 'chunk') {
            receiver.current?.chunk(body);
          }
        },
        ended: (reason) => {
          if (finished.current) setSenderGone(true);
          else stopWith({ step: 'ended', reason: reason === 'acknowledged' || reason === 'delivered' ? 'lost' : reason });
        },
        unavailable: () => {
          if (finished.current) {
            setSenderGone(true);
            return;
          }
          forgetFragment();
          stopWith({ step: 'gone' });
        },
        lost: () => {
          if (finished.current) setSenderGone(true);
          else stopWith({ step: 'ended', reason: 'lost' });
        },
      },
    });
    connection.current = conn;
    conn.connect();
  }

  function tryAgain() {
    receive();
    // The button replaced itself; put focus back at the top of what replaced it.
    requestAnimationFrame(() => heading.current?.focus());
  }

  async function accept(files: FileEntry[], code: string, toDisk: boolean) {
    const conn = connection.current;
    if (!conn) return;
    const sink = toDisk ? await pickDiskSink(files) : memorySink();
    if (!sink) {
      setState({ step: 'offer', code, files, noFolder: true });
      return;
    }
    const total = totalBytes(files);
    setState({ step: 'transferring', files, written: 0, total });
    const incoming = new FileReceiver(conn, files, sink, {
      // Re-render once per whole percent, not once per 64 KiB chunk.
      progress: (written) =>
        setState((current) => (current.step === 'transferring' && percent(written, total) !== percent(current.written, total) ? { ...current, written } : current)),
      done: (saved) => {
        finished.current = true;
        receiver.current = null;
        roomKey.current = null;
        forgetFragment();
        urls.current.push(...saved.flatMap((f) => (f.url ? [f.url] : [])));
        setState({ step: 'received', saved, disk: sink.kind === 'disk' });
      },
      failed: (reason) => stopWith({ step: 'failed', reason }),
    });
    receiver.current = incoming;
    await incoming.accept();
  }

  async function gotIt() {
    try {
      await connection.current?.send('acknowledged');
    } catch {
      setSenderGone(true);
      return;
    }
    setAcknowledged(true);
  }

  return (
    <main className={revealStyles.page}>
      {state.step === 'checking' && (
        <p className={ui.hint} role="status">
          {t('handoff.checking')}
        </p>
      )}

      {state.step === 'incomplete' && <Message heading={t('reveal.incomplete.title')} body={t('reveal.incomplete.body')} headingRef={heading} />}

      {state.step === 'gone' && <Message heading={t('files.gone.title')} body={t('files.gone.body')} headingRef={heading} />}

      {state.step === 'ended' && (
        <Message
          heading={t(state.reason === 'noDirect' ? 'files.noDirectTitle' : 'handoff.ended.title')}
          body={state.reason === 'noDirect' ? t('files.noDirect') : t(`handoff.ended.${state.reason}`)}
          headingRef={heading}
        >
          {state.reason === 'noDirect' && (
            <button type="button" className={ui.button} onClick={tryAgain}>
              {t('files.tryAgain')}
            </button>
          )}
        </Message>
      )}

      {state.step === 'failed' && <Message heading={t('files.failed.title')} body={t(`files.failed.${state.reason}`)} headingRef={heading} />}

      {(state.step === 'ready' || state.step === 'live') && (
        <section className={revealStyles.stack} aria-labelledby="files-title">
          <h1 id="files-title" className={revealStyles.title} ref={heading} tabIndex={-1}>
            {t('files.titleLine1')} <span className={revealStyles.accent}>{t('files.titleLine2')}</span>
          </h1>
          <p className={revealStyles.lead}>{t('files.lead')}</p>
          {state.step === 'ready' ? (
            <>
              <button type="button" className={ui.button} onClick={receive}>
                <FlameMark size={18} tone="onAccent" />
                {t('handoff.button')}
              </button>
              <div className={styles.notes}>
                <p className={ui.hint}>{t('files.botNote')}</p>
                <p className={ui.hint}>{t('handoff.ipNote')}</p>
              </div>
            </>
          ) : (
            <section className={styles.status} aria-label={t('files.label')}>
              <p className={styles.statusLine} role="status" data-testid="files-status">
                <span className={`${styles.dot} ${styles.dotWaiting}`} aria-hidden="true" />
                {t(state.phase === 'receiving' ? 'files.waitingFiles' : `handoff.${state.phase}`)}
              </p>
              {state.code && <Code code={state.code} />}
            </section>
          )}
        </section>
      )}

      {state.step === 'offer' && (
        <section className={revealStyles.stack} aria-labelledby="files-offer-title">
          <h1 id="files-offer-title" className={revealStyles.title} ref={heading} tabIndex={-1}>
            {t('files.offerTitle', { count: state.files.length })}
          </h1>
          <p className={revealStyles.lead}>{t('files.offerLead', { count: state.files.length, size: formatBytes(totalBytes(state.files)) })}</p>
          <FileList files={state.files.map((f) => ({ name: safeFileName(f.name), size: f.size }))} />
          {state.code && <Code code={state.code} />}
          {state.noFolder && (
            <p role="alert" className={ui.error}>
              {t('files.noFolder')}
            </p>
          )}
          {canSaveToDisk() ? (
            <>
              <button type="button" className={ui.button} onClick={() => void accept(state.files, state.code, true)}>
                {t(state.files.length === 1 ? 'files.chooseFile' : 'files.chooseFolder')}
              </button>
              <p className={ui.hint}>{t('files.diskHint')}</p>
            </>
          ) : (
            <>
              <button type="button" className={ui.button} onClick={() => void accept(state.files, state.code, false)}>
                {t('files.receiveToMemory')}
              </button>
              <p className={totalBytes(state.files) > MEMORY_WARNING_BYTES ? secretStyles.notice : ui.hint}>{t('files.memoryHint')}</p>
            </>
          )}
        </section>
      )}

      {state.step === 'transferring' && (
        <section className={revealStyles.stack} aria-labelledby="files-progress-title">
          <h1 id="files-progress-title" className={revealStyles.title} tabIndex={-1}>
            {t('files.receivingTitle')}
          </h1>
          <section className={styles.status} aria-label={t('files.label')}>
            <p className={styles.statusLine} role="status" data-testid="files-status">
              <span className={`${styles.dot} ${styles.dotWaiting}`} aria-hidden="true" />
              {t('files.receiving', { percent: tenths(state.written, state.total), done: formatBytes(state.written), total: formatBytes(state.total) })}
            </p>
            <progress className={styles.progress} value={state.written} max={Math.max(state.total, 1)} aria-label={t('files.progressLabel')} />
            <p className={ui.hint}>{t('files.keepOpen')}</p>
          </section>
        </section>
      )}

      {state.step === 'received' && (
        <section className={revealStyles.stack} aria-labelledby="files-received-title">
          <h1 id="files-received-title" className={revealStyles.title} ref={heading} tabIndex={-1}>
            {t('files.receivedTitle')}
          </h1>
          <p className={revealStyles.lead}>{t(state.disk ? 'files.savedToDisk' : 'files.saveEach')}</p>
          <FileList files={state.saved} />
          {!acknowledged && !senderGone && (
            <button type="button" className={ui.button} onClick={() => void gotIt()} aria-describedby="files-got-it-hint">
              {t('handoff.gotIt')}
            </button>
          )}
          <p className={secretStyles.notice} role="status" id="files-got-it-hint">
            {acknowledged ? t('files.done') : senderGone ? t('files.senderGone') : t('files.gotItHint')}
          </p>
        </section>
      )}
    </main>
  );
}

function Code({ code }: { code: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.code}>
      <span className={ui.eyebrow}>{t('handoff.code')}</span>
      <span className={styles.codeDigits} data-testid="files-code">
        {formatCode(code)}
      </span>
      <span className={ui.hint}>{t('handoff.codeHint')}</span>
    </div>
  );
}

function FileList({ files }: { files: { name: string; size: number; url?: string }[] }) {
  const { t } = useTranslation();
  return (
    <ul className={styles.fileList} data-testid="files-list">
      {files.map((file, i) => (
        <li key={`${i}-${file.name}`} className={styles.fileItem}>
          <span className={styles.fileName}>{file.name}</span>
          <span className={ui.hint}>{formatBytes(file.size)}</span>
          {file.url && (
            <a className={ui.buttonSecondary} href={file.url} download={file.name} aria-label={t('files.saveNamed', { name: file.name })}>
              {t('files.save')}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

interface MessageProps {
  heading: string;
  body: string;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  children?: React.ReactNode;
}

function Message({ heading, body, headingRef, children }: MessageProps) {
  return (
    <section className={revealStyles.stack}>
      <h1 className={revealStyles.title} ref={headingRef} tabIndex={-1}>
        {heading}
      </h1>
      <p className={revealStyles.lead}>{body}</p>
      {children}
    </section>
  );
}

const percent = (written: number, total: number): number => (total > 0 ? Math.floor((written / total) * 100) : 0);

/** Whole percent, in steps of 10 so the status line (a live region) changes rarely. */
const tenths = (written: number, total: number): number => (total > 0 ? Math.floor((written / total) * 10) * 10 : 0);
